import React from 'react'
import styled from '@emotion/styled'
import { Colors, Shapes, Levels, themeSpacing } from 'src/components/theme'
import { Flex } from 'src/components/flex-containers'
import { Typography } from '@mui/material'
import { ColorPallete, alphaColorPallete } from 'src/components/button/palettes'

export function makeTransparent(color: string | undefined, value: number) {
  if (color === undefined) {
    return 'transparent'
  }
  return color.replace(', 1)', `, ${value})`)
}

type AlertProps = {
  color?: keyof ColorPallete
  message?: string
  additionalMessage?: string
  actions?: React.ReactNode
}

type NameProps = {
  color: keyof ColorPallete
  text: string
}

const CustomAlert = styled(Flex)<{ color: keyof ColorPallete }>`
  align-items: center;
  justify-content: space-between;
  border-radius: 16px;
  padding: ${Shapes.spacing[1]}px ${Shapes.spacing[3]}px ${Shapes.spacing[1]}px ${Shapes.spacing[1]}px;
  background-color: ${(props) =>
    props.color ? alphaColorPallete[props.color].light : alphaColorPallete.inherit.light};
  border: 1px solid ${(props) => (props.color ? alphaColorPallete[props.color].dark : alphaColorPallete.inherit.dark)};
  box-shadow: ${Colors.BOX_SHADOW};
`

const Pill = styled(Flex)<{ color: keyof ColorPallete }>`
  background-color: ${(props) => (props.color ? alphaColorPallete[props.color].dark : alphaColorPallete.inherit.dark)};
  color: ${Colors.textBlack};
  padding: ${Shapes.spacing[2]}px;
  height: 24px;
  border-radius: 16px;
  align-items: center;
  justify-content: center;
  max-width: fit-content;
`

const NamePill = (props: NameProps) => {
  const { color, text } = props

  return (
    <Pill color={color}>
      <Typography variant="text1" color={Colors.textBlack}>
        {text}
      </Typography>
    </Pill>
  )
}

const TextWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: ${Shapes.spacing[2]}px;
  margin-right: ${Shapes.spacing[6]}px;
`

export const Alert = (props: AlertProps) => {
  const { color = 'error', message, additionalMessage, actions } = props

  return (
    <CustomAlert color={color}>
      <TextWrapper>
        <NamePill color={color} text={name(color)} />
        <Typography variant="text1" color={Colors.textBlack}>
          {message}
        </Typography>
        {additionalMessage && (
          <Typography flex={1} variant="text1" color={Colors.textDark}>
            {additionalMessage}
          </Typography>
        )}
      </TextWrapper>
      {actions}
    </CustomAlert>
  )
}

const BoxAlertWrapper = styled.div<AlertProps>`
  z-index: ${Levels.mapMessage};
  background-color: ${(props) =>
    props.color ? alphaColorPallete[props.color].light : alphaColorPallete.inherit.light};
  border: 2px solid;
  border-color: ${(props) => (props.color ? alphaColorPallete[props.color].dark : alphaColorPallete.inherit.dark)};
  padding: ${Shapes.spacing[4]}px;
  border-radius: 16px;
  align-items: center;
  justify-content: center;
  max-width: fit-content;
  display: flex;
  gap: ${Shapes.spacing[4]}px;
`

const AlertMessageWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${themeSpacing(1)}px;
`

const name = (severity: keyof ColorPallete) => {
  switch (severity) {
    case 'error':
      return 'Error'
    case 'success':
      return 'Success'
    case 'warning':
      return 'Warning'
    default:
      return 'Attention'
  }
}

type BoxAlertProps = AlertProps & {
  additionalMessage?: string
  tertiaryMessage?: string
}
export const BoxAlert = (props: BoxAlertProps) => {
  const { color = 'error', message, additionalMessage, tertiaryMessage } = props

  return (
    <BoxAlertWrapper color={color}>
      {message ? (
        <CustomAlert color={color}>
          <NamePill color={color} text={name(color)} />
          <Typography variant="text1" color={Colors.textBlack}>
            {message}
          </Typography>
        </CustomAlert>
      ) : (
        <NamePill color={color} text={name(color)} />
      )}
      <AlertMessageWrapper>
        {additionalMessage && (
          <Typography variant="text1" color={Colors.textDark}>
            {additionalMessage}
          </Typography>
        )}
        {tertiaryMessage && (
          <Typography variant="text1" color={Colors.textMedium}>
            {tertiaryMessage}
          </Typography>
        )}
      </AlertMessageWrapper>
    </BoxAlertWrapper>
  )
}
